/**
 * Character palette for the Rive **cartoons / animations** characters.
 *
 * The animation character (authored in Rive, 2400×1400) is recoloured at runtime
 * via data-bound ViewModel colour properties. Each of the app's 9 skin tones
 * needs a full SET of colours (not just one base), because the cartoon has
 * highlight / blush / shading / eye / lip detail that simple body-tinting never had.
 *
 * Authoring: the Character Editor (`/debug/character-editor`) edits these 9 sets
 * and exports this exact shape — paste the export's `set` values back into
 * `CHARACTER_SKIN_SETS`. (Eyebrow colours live per hair option in
 * `HAIR_COLOURS`, not here.)
 *
 * Live app: the cartoon uses the child's ONBOARDING skin selection
 * (`students.skin_color`, a hex from `SKIN_COLORS`) → `skinSetForHex(hex)` returns
 * the full set to apply. The editor's tone ramp is only for previewing each set.
 *
 * Values below were authored in the editor and exported (character-palette.json,
 * 2026-08-18). `baseHex` is the app's FIXED onboarding tone (identity / lookup
 * key), which is NOT the same as the editable `set.skinBase` (see tone index 7).
 */
import { SKIN_COLORS } from './skinColors';

/**
 * RUNTIME SOURCING (live cartoons/animations section — planned, not built yet).
 * The cartoon character is assembled at runtime from two sources:
 *
 *  From ONBOARDING:
 *    • gender      → `isGirl` bool binding (toggles R/L-Eyelashes visibility)
 *    • skin tone   → `skinSetForHex(students.skin_color)` (below) → the 9 colour props
 *    • eye colour  → EYE_COLOURS option → pupil image  (FUTURE onboarding field)
 *
 *  From the PHOTO-ANALYSIS tool (future):
 *    • eye shape (1|2)          → `eyeShape` enum binding
 *    • freckles (yes|no)        → `freckles` bool binding
 *    • glasses (yes|no)         → `eyeglasses` bool binding
 *    • hair front / over / back → HAIR_COLOURS option images (+ eyebrow colour)
 *
 * The editor authors the DATA (skin sets here; eye/hair colour options, per-option
 * images and eyebrow in `characterEditor/contract.ts`) and already surfaces the
 * bool/enum controls the moment they exist. PREREQUISITES still missing in the
 * `.riv`: the `isGirl`, `eyeShape`, `freckles`, `eyeglasses` bindings (only the 9
 * colours + eyebrow exist today), and committed per-option eye/hair PNGs.
 */

/** The per-tone skin colour components — bound ViewModel color props on the Rive
 *  file (section "skin"). `eyebrow` is NOT here: it tracks hair, not skin tone. */
export const SKIN_SET_KEYS = [
  'skinBase',
  'skinHighlight',
  'blush',
  'skinLineShade',
  'eyeShade',
  'eyeLashes',
  'mouthLine',
  'lips',
] as const;
export type SkinSetKey = (typeof SKIN_SET_KEYS)[number];
export type SkinSet = Record<SkinSetKey, string>;

export interface SkinTonePalette {
  /** 0 … 8, matching the order of `SKIN_COLORS` (darkest → lightest). */
  index: number;
  /** The app's canonical base hex for this tone — i.e. the onboarding value. */
  baseHex: string;
  set: SkinSet;
}

/** The 9 authored skin sets, ordered to match `SKIN_COLORS` (index 0 → 8). */
const CHARACTER_SKIN_SETS: SkinSet[] = [
  { skinBase: '#432D1E', skinHighlight: '#6E472C', blush: '#A14F32', skinLineShade: '#291505', eyeShade: '#3B220B', eyeLashes: '#000000', mouthLine: '#210F08', lips: '#47261A' },
  { skinBase: '#644029', skinHighlight: '#8F5A35', blush: '#B35E34', skinLineShade: '#4F2406', eyeShade: '#633912', eyeLashes: '#331600', mouthLine: '#3D1C0F', lips: '#613824' },
  { skinBase: '#7F5633', skinHighlight: '#AD7349', blush: '#D67654', skinLineShade: '#632D07', eyeShade: '#824E1D', eyeLashes: '#421D00', mouthLine: '#542715', lips: '#804528' },
  { skinBase: '#96653A', skinHighlight: '#BA7A4C', blush: '#EB8B75', skinLineShade: '#75411C', eyeShade: '#965C26', eyeLashes: '#4D2200', mouthLine: '#612D18', lips: '#914F2C' },
  { skinBase: '#BB8052', skinHighlight: '#D69D67', blush: '#F79C7E', skinLineShade: '#965A2F', eyeShade: '#A86C34', eyeLashes: '#542600', mouthLine: '#753F2A', lips: '#B56D47' },
  { skinBase: '#DDA679', skinHighlight: '#EDC39D', blush: '#FFB2A8', skinLineShade: '#B57141', eyeShade: '#C98B51', eyeLashes: '#542600', mouthLine: '#854636', lips: '#D18962' },
  { skinBase: '#FFCC91', skinHighlight: '#FFE6BD', blush: '#FFBEB5', skinLineShade: '#D1854B', eyeShade: '#ED9A5F', eyeLashes: '#5C2E08', mouthLine: '#945A3D', lips: '#F29F80' },
  { skinBase: '#FFD0A5', skinHighlight: '#FFE5CC', blush: '#FFBBB5', skinLineShade: '#DE8F57', eyeShade: '#E4A76E', eyeLashes: '#5C2E08', mouthLine: '#A55C44', lips: '#EDAB85' },
  { skinBase: '#F5D4B4', skinHighlight: '#FFEBD9', blush: '#FFB6BC', skinLineShade: '#E6A083', eyeShade: '#E4A76E', eyeLashes: '#6E230A', mouthLine: '#C97C67', lips: '#E6AB97' },
];

/** baseHex is always the app's fixed onboarding tone (identity / lookup key). */
export const CHARACTER_SKIN_PALETTE: SkinTonePalette[] = SKIN_COLORS.map((hex, index) => ({
  index,
  baseHex: hex,
  set: CHARACTER_SKIN_SETS[index],
}));

/**
 * Live-app lookup: given the child's onboarding skin-colour hex, return the full
 * set to apply to the cartoon. Falls back to the lightest tone if unmatched.
 */
export function skinSetForHex(hex: string | null | undefined): SkinSet {
  const norm = (hex ?? '').trim().toUpperCase();
  const match = CHARACTER_SKIN_PALETTE.find((p) => p.baseHex.toUpperCase() === norm);
  return (match ?? CHARACTER_SKIN_PALETTE[CHARACTER_SKIN_PALETTE.length - 1]).set;
}
